import {
	isMixedMetricSeriesTypes,
	normalizeMetricSeriesTypes,
	resolveMetricSeriesDefaultType,
} from '../../shared/lib/metricSeriesTypes';
import {
	getPrimaryPhaseMetricLabel,
	isPrimaryPhaseCurrencyMetric,
	ORDER_PRIMARY_PHASE_METRICS,
} from '../../pipeline/lib/orderPrimaryPhaseMetric';
import { TAIL_QUASI_RECEIVABLE_LABEL } from '../../../../../utils/order/orderAccountsReceivable';
import {
	CHART_BAR_ORIENTATION,
	CHART_BAR_ORIENTATION_OPTIONS,
	normalizeChartBarOrientation,
} from '../../shared/lib/barOrientation';

export const COMPOSITION_CHART_DIMENSION = {
	IS_COMPLETED: 'isCompleted',
	PRIMARY_PHASE: 'primaryPhase',
	APPROVAL_STATUS: 'approvalStatus',
	PAYMENT_STATUS: 'paymentStatus',
	DELIVERY_STATUS: 'deliveryStatus',
	PAYMENT_METHOD: 'paymentMethod',
	SALE_USER: 'saleUser',
	ASSISTANT_USER: 'assistantUser',
	CUSTOMER: 'customer',
	CUSTOMER_CONTACT: 'customerContact',
	PROVINCE: 'province',
	BRAND: 'brand',
	SUPPLIER: 'supplier',
	CATEGORY: 'category',
	PRODUCT_NAME: 'productName',
	PRODUCT_CODE: 'productCode',
};

export const COMPOSITION_CHART_DIMENSION_GROUP = {
	STATUS: 'status',
	ENTITY: 'entity',
	MATERIAL: 'material',
};

export const COMPOSITION_CHART_DIMENSION_GROUP_LABELS = {
	[COMPOSITION_CHART_DIMENSION_GROUP.STATUS]: '状态',
	[COMPOSITION_CHART_DIMENSION_GROUP.ENTITY]: '对象',
	[COMPOSITION_CHART_DIMENSION_GROUP.MATERIAL]: '物料',
};

/** 扇区顺序固定的维度（多指标环形/饼图需保持各环对齐） */
export const CUSTOM_CHART_FIXED_SLICE_ORDER = {
	[COMPOSITION_CHART_DIMENSION.IS_COMPLETED]: [
		{ key: 'completed', label: '已完成' },
		{ key: 'notCompleted', label: '未完成' },
	],
};

export const isCompositionChartFixedSliceDimension = (dimension) => Boolean(CUSTOM_CHART_FIXED_SLICE_ORDER[dimension]);

export const getCompositionChartFixedSliceOrder = (dimension) => CUSTOM_CHART_FIXED_SLICE_ORDER[dimension] ?? [];

export const COMPOSITION_CHART_METRIC = ORDER_PRIMARY_PHASE_METRICS;

export const CUSTOM_CHART_MAX_METRICS = 3;

export const COMPOSITION_CHART_TYPE = {
	DONUT: 'donut',
	PIE: 'pie',
	FUNNEL: 'funnel',
	BAR: 'bar',
	LINE: 'line',
	MAP: 'map',
};

/** 组成柱状图默认纵向 */
export const CUSTOM_CHART_BAR_DEFAULT_ORIENTATION = CHART_BAR_ORIENTATION.VERTICAL;

/** 组成报表 · 状态类拆分（与顶栏订单状态筛选项对齐：仅流程节点） */
export const CUSTOM_CHART_STATUS_DIMENSION_OPTIONS = [
	{ value: COMPOSITION_CHART_DIMENSION.PRIMARY_PHASE, label: '流程节点', group: COMPOSITION_CHART_DIMENSION_GROUP.STATUS },
];

/** 组成报表 · 对象类拆分（与顶栏客户/团队筛选项、经营总览排行对齐） */
export const CUSTOM_CHART_ENTITY_DIMENSION_OPTIONS = [
	{ value: COMPOSITION_CHART_DIMENSION.CUSTOMER, label: '单位', group: COMPOSITION_CHART_DIMENSION_GROUP.ENTITY },
	{ value: COMPOSITION_CHART_DIMENSION.CUSTOMER_CONTACT, label: '联系人', group: COMPOSITION_CHART_DIMENSION_GROUP.ENTITY },
	{ value: COMPOSITION_CHART_DIMENSION.SALE_USER, label: '销售员', group: COMPOSITION_CHART_DIMENSION_GROUP.ENTITY },
	{ value: COMPOSITION_CHART_DIMENSION.ASSISTANT_USER, label: '销售助理', group: COMPOSITION_CHART_DIMENSION_GROUP.ENTITY },
	{ value: COMPOSITION_CHART_DIMENSION.PROVINCE, label: '省份', group: COMPOSITION_CHART_DIMENSION_GROUP.ENTITY },
];

/** 组成报表 · 物料类拆分（订单行展开） */
export const CUSTOM_CHART_MATERIAL_DIMENSION_OPTIONS = [
	{ value: COMPOSITION_CHART_DIMENSION.SUPPLIER, label: '厂家', group: COMPOSITION_CHART_DIMENSION_GROUP.MATERIAL },
	{ value: COMPOSITION_CHART_DIMENSION.BRAND, label: '品牌', group: COMPOSITION_CHART_DIMENSION_GROUP.MATERIAL },
	{ value: COMPOSITION_CHART_DIMENSION.CATEGORY, label: '品类', group: COMPOSITION_CHART_DIMENSION_GROUP.MATERIAL },
	{ value: COMPOSITION_CHART_DIMENSION.PRODUCT_NAME, label: '品名', group: COMPOSITION_CHART_DIMENSION_GROUP.MATERIAL },
	{ value: COMPOSITION_CHART_DIMENSION.PRODUCT_CODE, label: '货号', group: COMPOSITION_CHART_DIMENSION_GROUP.MATERIAL },
];

/** 横轴可选：顶栏可筛且已支持拆分的维度 */
export const COMPOSITION_CHART_DIMENSION_OPTIONS = [
	...CUSTOM_CHART_ENTITY_DIMENSION_OPTIONS,
	...CUSTOM_CHART_MATERIAL_DIMENSION_OPTIONS,
	...CUSTOM_CHART_STATUS_DIMENSION_OPTIONS,
];

/** 订单行展开维度：金额按行合计，单数按订单去重 */
export const COMPOSITION_CHART_LINE_ITEM_DIMENSIONS = new Set([
	COMPOSITION_CHART_DIMENSION.SUPPLIER,
	COMPOSITION_CHART_DIMENSION.CATEGORY,
	COMPOSITION_CHART_DIMENSION.PRODUCT_NAME,
	COMPOSITION_CHART_DIMENSION.PRODUCT_CODE,
	COMPOSITION_CHART_DIMENSION.BRAND,
]);

/** 旧图仍可能用到的维度，校验时放行但不出现在横轴选择 */
export const COMPOSITION_CHART_LEGACY_DIMENSION_VALUES = new Set([
	COMPOSITION_CHART_DIMENSION.IS_COMPLETED,
	COMPOSITION_CHART_DIMENSION.APPROVAL_STATUS,
	COMPOSITION_CHART_DIMENSION.PAYMENT_STATUS,
	COMPOSITION_CHART_DIMENSION.DELIVERY_STATUS,
	COMPOSITION_CHART_DIMENSION.PAYMENT_METHOD,
]);

export const COMPOSITION_CHART_METRIC_OPTIONS = [
	{ value: COMPOSITION_CHART_METRIC.COUNT, label: '单数' },
	{ value: COMPOSITION_CHART_METRIC.TOTAL_PRICE, label: '合同金额' },
	{ value: COMPOSITION_CHART_METRIC.RECEIVABLE, label: '应收账款' },
	{ value: COMPOSITION_CHART_METRIC.QUASI_RECEIVABLE, label: TAIL_QUASI_RECEIVABLE_LABEL },
	{ value: COMPOSITION_CHART_METRIC.PENDING_PREPAYMENT, label: '待收预付' },
	{ value: COMPOSITION_CHART_METRIC.PAID_PREPAYMENT, label: '已收预付' },
];

const VALID_COMPOSITION_CHART_METRIC_VALUES = new Set(COMPOSITION_CHART_METRIC_OPTIONS.map((item) => item.value));

export const COMPOSITION_CHART_METRIC_DISPLAY_ORDER = COMPOSITION_CHART_METRIC_OPTIONS.map((item) => item.value);

export const DEFAULT_COMPOSITION_CHART_METRICS = [COMPOSITION_CHART_METRIC.COUNT];

export const sortCompositionChartMetrics = (metrics = []) =>
	COMPOSITION_CHART_METRIC_DISPLAY_ORDER.filter((metric) => metrics.includes(metric));

export const isValidCompositionChartMetric = (metric) => VALID_COMPOSITION_CHART_METRIC_VALUES.has(metric);

export const normalizeCompositionChartMetrics = (input) => {
	let raw = input;
	if (input && typeof input === 'object' && !Array.isArray(input)) {
		raw = input.metrics ?? input.metric;
	}
	if (typeof raw === 'string' && raw.trim()) {
		raw = [raw];
	}
	if (!Array.isArray(raw)) {
		return [...DEFAULT_COMPOSITION_CHART_METRICS];
	}
	const next = [];
	for (const metric of raw) {
		if (!isValidCompositionChartMetric(metric) || next.includes(metric)) continue;
		next.push(metric);
		if (next.length >= CUSTOM_CHART_MAX_METRICS) break;
	}
	return next.length ? sortCompositionChartMetrics(next) : [...DEFAULT_COMPOSITION_CHART_METRICS];
};

export const resolvePrimaryCompositionChartMetric = (metrics) =>
	normalizeCompositionChartMetrics(metrics)[0] ?? COMPOSITION_CHART_METRIC.COUNT;

export const toggleCompositionChartMetric = (activeMetrics, metric) => {
	if (!isValidCompositionChartMetric(metric)) return normalizeCompositionChartMetrics(activeMetrics);
	const normalized = normalizeCompositionChartMetrics(activeMetrics);
	if (normalized.includes(metric)) {
		if (normalized.length <= 1) return normalized;
		return sortCompositionChartMetrics(normalized.filter((item) => item !== metric));
	}
	if (normalized.length >= CUSTOM_CHART_MAX_METRICS) return normalized;
	return sortCompositionChartMetrics([...normalized, metric]);
};

export const formatCompositionChartMetricsLabel = (metrics = [], separator = ' / ') =>
	normalizeCompositionChartMetrics(metrics)
		.map((metric) => getPrimaryPhaseMetricLabel(metric))
		.join(separator);

/** 组成图多指标序列绑定（含单数/金额双轴）；勿复用趋势折线的 normalizeTrendYAxisMetrics */
export const resolveCompositionChartMetricBindings = (metrics = []) => {
	const sorted = normalizeCompositionChartMetrics(metrics);
	const hasCount = sorted.includes(ORDER_PRIMARY_PHASE_METRICS.COUNT);
	const amountMetrics = sorted.filter((metric) => isPrimaryPhaseCurrencyMetric(metric));
	const useDualAxis = hasCount && amountMetrics.length > 0;

	return sorted.map((metric) => ({
		metric,
		label: getPrimaryPhaseMetricLabel(metric),
		currencyMetric: isPrimaryPhaseCurrencyMetric(metric),
		yAxisIndex: useDualAxis && metric === ORDER_PRIMARY_PHASE_METRICS.COUNT ? 0 : useDualAxis ? 1 : 0,
	}));
};

export const resolveCompositionChartYAxisLabels = (bindings = []) => {
	const hasCountAxis = bindings.some((item) => item.yAxisIndex === 0 && !item.currencyMetric);
	const hasAmountAxis = bindings.some((item) => item.currencyMetric);
	const useDualAxis = hasCountAxis && hasAmountAxis;
	return {
		useDualAxis,
		leftName: hasCountAxis ? '单数' : '',
		rightName: useDualAxis && hasAmountAxis ? '金额' : hasAmountAxis ? getPrimaryPhaseMetricLabel(bindings.find((item) => item.currencyMetric)?.metric) : '',
	};
};

export const isCompositionChartMultiMetricCompareType = (chartType) =>
	chartType === COMPOSITION_CHART_TYPE.BAR || chartType === COMPOSITION_CHART_TYPE.LINE;

/** 组成报表 · 占比类（份额/结构） */
export const CUSTOM_CHART_SHARE_TYPE_OPTIONS = [
	{ value: COMPOSITION_CHART_TYPE.DONUT, label: '环形' },
	{ value: COMPOSITION_CHART_TYPE.PIE, label: '饼图' },
	{ value: COMPOSITION_CHART_TYPE.FUNNEL, label: '漏斗' },
];

/** 组成报表 · 对比类（排行/趋势） */
export const CUSTOM_CHART_COMPARE_TYPE_OPTIONS = [
	{ value: COMPOSITION_CHART_TYPE.BAR, label: '柱状' },
	{ value: COMPOSITION_CHART_TYPE.LINE, label: '折线' },
];

/** 组成报表 · 地理类（客户/省份） */
export const CUSTOM_CHART_MAP_TYPE_OPTIONS = [{ value: COMPOSITION_CHART_TYPE.MAP, label: '地图' }];

export const CUSTOM_CHART_GEO_DIMENSIONS = new Set([COMPOSITION_CHART_DIMENSION.CUSTOMER, COMPOSITION_CHART_DIMENSION.PROVINCE]);

export const COMPOSITION_CHART_TYPE_OPTIONS = [...CUSTOM_CHART_SHARE_TYPE_OPTIONS, ...CUSTOM_CHART_COMPARE_TYPE_OPTIONS];

export const isCompositionChartGeoDimension = (dimension) => CUSTOM_CHART_GEO_DIMENSIONS.has(dimension);

export const resolveCompositionChartTypeOptions = (dimension) =>
	isCompositionChartGeoDimension(dimension) ? [...COMPOSITION_CHART_TYPE_OPTIONS, ...CUSTOM_CHART_MAP_TYPE_OPTIONS] : COMPOSITION_CHART_TYPE_OPTIONS;

export const isCompositionChartBarType = (chartType) => chartType === COMPOSITION_CHART_TYPE.BAR;

export const isCompositionChartPieLikeType = (chartType) =>
	chartType === COMPOSITION_CHART_TYPE.DONUT || chartType === COMPOSITION_CHART_TYPE.PIE;

export const isCompositionChartFunnelType = (chartType) => chartType === COMPOSITION_CHART_TYPE.FUNNEL;

export const isCompositionChartLineType = (chartType) => chartType === COMPOSITION_CHART_TYPE.LINE;

export const isCompositionChartMapType = (chartType) => chartType === COMPOSITION_CHART_TYPE.MAP;

/** 漏斗/地图仅渲染主指标，多选会被静默忽略 */
export const isCompositionChartSingleMetricChartType = (chartType) =>
	chartType === COMPOSITION_CHART_TYPE.FUNNEL || chartType === COMPOSITION_CHART_TYPE.MAP;

/** 维度与指标语义重叠：财务状态已表达应收口径，不宜再选应收类指标 */
export const COMPOSITION_CHART_DIMENSION_METRIC_CONFLICTS = {
	[COMPOSITION_CHART_DIMENSION.PAYMENT_STATUS]: [COMPOSITION_CHART_METRIC.RECEIVABLE, COMPOSITION_CHART_METRIC.QUASI_RECEIVABLE],
};

export const isCompositionChartMetricConflictForDimension = (dimension, metric) =>
	(COMPOSITION_CHART_DIMENSION_METRIC_CONFLICTS[dimension] ?? []).includes(metric);

const filterCompositionChartMetricsForDimension = (metrics, dimension) => {
	const filtered = metrics.filter((metric) => !isCompositionChartMetricConflictForDimension(dimension, metric));
	return filtered.length ? sortCompositionChartMetrics(filtered) : [...DEFAULT_COMPOSITION_CHART_METRICS];
};

const clampCompositionChartMetricsForChartType = (metrics, chartType) => {
	if (!isCompositionChartSingleMetricChartType(chartType)) return metrics;
	return [resolvePrimaryCompositionChartMetric(metrics)];
};

export const resolveCompositionChartMetricDisableReason = ({
	dimension,
	activeMetrics,
	metric,
	maxMetrics = CUSTOM_CHART_MAX_METRICS,
}) => {
	if (isCompositionChartMetricConflictForDimension(dimension, metric)) {
		return '财务状态维度与应收账款/准应收含义重叠';
	}
	if (!activeMetrics.includes(metric) && activeMetrics.length >= maxMetrics) {
		return `最多选择 ${maxMetrics} 个指标`;
	}
	return '';
};

export const getCompositionChartCompositionHints = (config = {}) => {
	const normalized = normalizeCompositionChartConfig(config);
	const hints = [];

	if (isCompositionChartLineType(normalized.chartType)) {
		hints.push('分类维度用折线易误解为趋势，建议改用柱状');
	}

	const amountMetricCount = normalized.metrics.filter((metric) => isPrimaryPhaseCurrencyMetric(metric)).length;
	if (
		amountMetricCount >= 2 &&
		!normalized.metrics.includes(COMPOSITION_CHART_METRIC.COUNT) &&
		isCompositionChartMultiMetricCompareType(normalized.chartType)
	) {
		hints.push('多个金额指标共用纵轴，量级差异大时难分辨，建议加入单数或只保留一项金额');
	}

	if (normalized.chartType === COMPOSITION_CHART_TYPE.FUNNEL && normalized.dimension === COMPOSITION_CHART_DIMENSION.IS_COMPLETED) {
		hints.push('完成状态仅两档，漏斗效果有限，建议用环形或柱状');
	}

	return hints;
};

export { CHART_BAR_ORIENTATION_OPTIONS };

export const DEFAULT_COMPOSITION_CHART_CONFIG = {
	dimension: COMPOSITION_CHART_DIMENSION.PRIMARY_PHASE,
	metrics: [...DEFAULT_COMPOSITION_CHART_METRICS],
	metric: COMPOSITION_CHART_METRIC.COUNT,
	chartType: COMPOSITION_CHART_TYPE.DONUT,
	barOrientation: CUSTOM_CHART_BAR_DEFAULT_ORIENTATION,
	metricSeriesTypes: {},
};

const isValidDimension = (value) =>
	COMPOSITION_CHART_DIMENSION_OPTIONS.some((item) => item.value === value) || COMPOSITION_CHART_LEGACY_DIMENSION_VALUES.has(value);
const isValidChartType = (value, dimension) => {
	if (value === COMPOSITION_CHART_TYPE.MAP) return isCompositionChartGeoDimension(dimension);
	return COMPOSITION_CHART_TYPE_OPTIONS.some((item) => item.value === value);
};

export const normalizeCompositionChartConfig = (config = {}) => {
	const dimension = isValidDimension(config.dimension) ? config.dimension : DEFAULT_COMPOSITION_CHART_CONFIG.dimension;
	let chartType = isValidChartType(config.chartType, dimension) ? config.chartType : DEFAULT_COMPOSITION_CHART_CONFIG.chartType;
	if (chartType === COMPOSITION_CHART_TYPE.MAP && !isCompositionChartGeoDimension(dimension)) {
		chartType = COMPOSITION_CHART_TYPE.BAR;
	}

	let metrics = normalizeCompositionChartMetrics(config.metrics ?? config.metric);
	metrics = filterCompositionChartMetricsForDimension(metrics, dimension);
	metrics = clampCompositionChartMetricsForChartType(metrics, chartType);
	const metric = resolvePrimaryCompositionChartMetric(metrics);
	const metricSeriesTypes = normalizeCompositionChartMetricSeriesTypes(metrics, config.metricSeriesTypes, chartType);

	return {
		dimension,
		metrics,
		metric,
		chartType,
		barOrientation: normalizeChartBarOrientation(config.barOrientation, CUSTOM_CHART_BAR_DEFAULT_ORIENTATION),
		metricSeriesTypes,
		compareSeries: Boolean(config.compareSeries),
	};
};

const COMPOSITION_CHART_LEGACY_DIMENSION_LABELS = {
	[COMPOSITION_CHART_DIMENSION.IS_COMPLETED]: '完成状态',
	[COMPOSITION_CHART_DIMENSION.APPROVAL_STATUS]: '审批状态',
	[COMPOSITION_CHART_DIMENSION.PAYMENT_METHOD]: '支付方式',
	[COMPOSITION_CHART_DIMENSION.PROVINCE]: '省份',
};

export const getCompositionChartDimensionLabel = (dimension) =>
	COMPOSITION_CHART_DIMENSION_OPTIONS.find((item) => item.value === dimension)?.label ??
	COMPOSITION_CHART_LEGACY_DIMENSION_LABELS[dimension] ??
	'维度';

export const getCompositionChartMetricLabel = (metric) => getPrimaryPhaseMetricLabel(metric);

export const isCompositionChartMultiMetricMixedType = (config = {}) => {
	const normalized = normalizeCompositionChartConfig(config);
	if (!isCompositionChartMultiMetricCompareType(normalized.chartType)) return false;
	const seriesTypes = normalizeCompositionChartMetricSeriesTypes(normalized.metrics, normalized.metricSeriesTypes, normalized.chartType);
	return isMixedMetricSeriesTypes(seriesTypes);
};

export const normalizeCompositionChartMetricSeriesTypes = (metrics, input, chartType) =>
	normalizeMetricSeriesTypes(metrics, input, {
		defaultType: resolveMetricSeriesDefaultType(chartType, isCompositionChartBarType),
	});

export const getCompositionChartTypeLabel = (chartType, { mixed = false } = {}) => {
	if (mixed) return '柱折混合';
	return (
		resolveCompositionChartTypeOptions(COMPOSITION_CHART_DIMENSION.CUSTOMER).find((item) => item.value === chartType)?.label ??
		COMPOSITION_CHART_TYPE_OPTIONS.find((item) => item.value === chartType)?.label ??
		'环形'
	);
};
